// Helper component for expandable list display with overflow handling
function ExpandableListDisplay({ list, label }) {
  const [expanded, setExpanded] = React.useState(false);
  const textRef = React.useRef(null);
  const [needsExpansion, setNeedsExpansion] = React.useState(false);

  // Check if text is truncated or if there are too many items
  React.useEffect(() => {
    const joinedText = list.join(", ");
    const hasMany = list.length > 10; // More than 10 items
    const hasLongText = joinedText.length > 150; // Text longer than 150 characters

    setNeedsExpansion(hasMany || hasLongText);
  }, [list]);

  // If expansion is not needed, show simple display
  if (!needsExpansion) {
    return (
      <p className="w-full">
        <span className="font-semibold">{label}:</span> {list.join(", ")}
      </p>
    );
  }

  if (expanded) {
    return (
      <div className="w-full">
        <span className="font-semibold">{label}:</span>{" "}
        <div
          className="mt-1 max-h-32 overflow-y-auto whitespace-pre-line break-words rounded border border-gray-200 bg-gray-50 px-2 py-1"
          style={{ lineHeight: "1.5" }}
        >
          {list.join(", ")}
        </div>
        <button
          type="button"
          className="mt-1 text-xs text-primary1 hover:underline"
          onClick={() => setExpanded(false)}
        >
          Collapse
        </button>
      </div>
    );
  } else {
    return (
      <div className="flex w-full items-center">
        <span className="mr-1 font-semibold">{label}:</span>
        <span
          ref={textRef}
          className="max-w-[80%] overflow-hidden truncate text-ellipsis whitespace-nowrap"
          title={list.join(", ")}
        >
          {list.join(", ")}
        </span>
        <button
          type="button"
          className="ml-2 shrink-0 text-sm text-primary1 hover:underline"
          onClick={() => setExpanded(true)}
        >
          Expand
        </button>
      </div>
    );
  }
}

// Helper component for cast display with expand/collapse and overflow handling
function CastDisplay({ castList }) {
  const [castExpanded, setCastExpanded] = React.useState(false);
  if (castExpanded) {
    return (
      <div className="w-full">
        <span className="font-semibold">Cast:</span>{" "}
        <div
          className="mt-1 max-h-32 overflow-y-auto whitespace-pre-line break-words rounded border border-gray-200 bg-gray-50 px-2 py-1 "
          style={{ lineHeight: "1.5" }}
        >
          {castList.join(", ")}
        </div>
        <button
          type="button"
          className="mt-1 text-xs text-primary1 hover:underline"
          onClick={() => setCastExpanded(false)}
        >
          Collapse
        </button>
      </div>
    );
  } else {
    return (
      <div className="flex w-full items-center">
        <span className="mr-1 font-semibold">Cast:</span>
        <span
          className="max-w-[80%] overflow-hidden truncate text-ellipsis whitespace-nowrap"
          title={castList.join(", ")}
        >
          {castList.join(", ")}
        </span>
        <button
          type="button"
          className="ml-2 shrink-0 text-sm text-primary1 hover:underline"
          onClick={() => setCastExpanded(true)}
        >
          Expand
        </button>
      </div>
    );
  }
}

import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { HiEye, HiEyeOff, HiTrash } from "react-icons/hi";
import { getCookie } from "../../reducer/utils";
import { toast } from "react-toastify";
import EditFutureOriginal from "../../Components/SideFilters/EditFutureOriginal";
import ConfirmModal from "../../Components/Modal/ConfirmModal";

const statusLabelMap = {
  PENDING: "Pending",
  PROCESSING: "Processing",
  TO_BE_RELEASED: "To Be Released",
  COMPLETED: "Completed",
  FAILED: "Failed",
  ERROR: "Error",
};

const getStatusColor = (status) => {
  switch (status) {
    case "PENDING":
      return " text-yellow-700";
    case "PROCESSING":
      return " text-blue-700";
    case "TO_BE_RELEASED":
      return " text-purple-700";
    case "COMPLETED":
      return " text-green-700";
    case "FAILED":
    case "ERROR":
      return " text-red-700";
    default:
      return "text-yellow-700";
  }
};

function FutureOriginalCard({ futureOriginal, onToggleHidden, onDelete }) {
  const baseURL = process.env.REACT_APP_BASE_URL;

  const [futureOriginalState, setFutureOriginalState] =
    useState(futureOriginal);
  const [roles, setRoles] = useState(getCookie("roles"));
  const toastId = useRef(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDuplicateFOForm, setShowDuplicateFOForm] = useState(false);
  // Modal state for confirmation
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingVerifyCountry, setPendingVerifyCountry] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  // Notes editing state
  const [editingNotesCountry, setEditingNotesCountry] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");

  const countryVerificationMap = useMemo(() => {
    const map = {};
    futureOriginalState.future_original_country_mapping.forEach((country) => {
      // If any episode is not verified, country is not verified
      const allVerified = futureOriginalState.is_series
        ? country.release_dates?.every((ep) => ep.is_verified)
        : country.movie_is_verified;
      map[country.country_code] = allVerified;
    });
    return map;
  }, [futureOriginalState]);

  const checkRole = (role) => {
    return roles.includes(role);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    const dateObj = new Date(date);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleString("en-US", { month: "short" });
    const year = dateObj.getFullYear().toString().slice(-2);
    return `${day < 10 ? "0" + day : day} ${month}, ${year}`;
  };

  const getFutureOriginal = async () => {
    const responseGetFutureOriginal = await fetch(
      `${baseURL}/api/v4/vod/future-originals/${futureOriginalState.id}?include_episode_status=true`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${getCookie("token")}`,
        },
      },
    );
    const futureOriginalData = await responseGetFutureOriginal.json();
    setFutureOriginalState(futureOriginalData);
  };

  // Hide/unhide toggles the `is_hidden` flag via the API, then refreshes the
  // list (the hidden FO drops out of the default list server-side).
  const handleToggleHidden = async () => {
    const newHidden = !futureOriginalState.is_hidden;
    toastId.current = toast.loading(
      newHidden ? "Hiding future original..." : "Unhiding future original...",
    );
    try {
      const response = await fetch(
        `${baseURL}/internal/api/v4/vod/future-originals/${futureOriginalState.id}/hidden?is_hidden=${newHidden}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${getCookie("token")}`,
          },
        },
      );
      if (!response.ok) throw new Error("Request failed");

      setFutureOriginalState((prev) => ({ ...prev, is_hidden: newHidden }));
      if (onToggleHidden) onToggleHidden(futureOriginalState.id);

      toast.update(toastId.current, {
        render: newHidden
          ? "Future Original hidden"
          : "Future Original unhidden",
        type: "success",
        isLoading: false,
        autoClose: 1000,
      });
    } catch (e) {
      toast.update(toastId.current, {
        render: "Failed to update hidden status",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  const updateIsVerified = async (countryCode, isVerified) => {
    toastId.current = toast.loading("Updating is verified...");

    const response = await fetch(
      `${baseURL}/internal/api/v4/vod/future-originals/is-verified`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getCookie("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          future_originals: [
            {
              id: futureOriginalState.id,
              country_mapping: [
                {
                  country_code: countryCode,
                  is_verified: isVerified,
                  is_manual_update: true,
                },
              ],
            },
          ],
        }),
      },
    );
    const data = await response.json();

    await getFutureOriginal();

    toast.update(toastId.current, {
      render: "Is verified updated successfully",
      type: "success",
      isLoading: false,
      autoClose: 1000,
    });
  };

  useEffect(() => {
    if (
      futureOriginalState.future_original_country_mapping.length > 0 &&
      selectedCountry === null
    ) {
      setSelectedCountry(
        futureOriginalState.future_original_country_mapping[0].country_code,
      );
    }
  }, [futureOriginalState]);

  // Only call updateIsVerified after confirmation
  const handleIsVerified = (countryCode, isVerified) => {
    futureOriginalState.future_original_country_mapping.map((country) => {
      if (country.country_code === countryCode) {
        country.is_verified = isVerified;
      }
    });
    updateIsVerified(countryCode, isVerified);
  };

  // Handler for Yes radio button
  const handleVerifyYesClick = (countryCode) => {
    setPendingVerifyCountry(countryCode);
    setConfirmModalOpen(true);
  };

  // Handler for modal confirm
  const handleConfirmVerify = () => {
    if (pendingVerifyCountry) {
      handleIsVerified(pendingVerifyCountry, true);
      setPendingVerifyCountry(null);
    }
  };

  // Handler for delete modal confirm. `reason` is the optional free-text reason
  // recorded on the FO and any affected requests, sent as a query param.
  const handleConfirmDelete = async (reason) => {
    toastId.current = toast.loading("Deleting future original...");
    try {
      const query = reason ? `?reason=${encodeURIComponent(reason)}` : "";
      const response = await fetch(
        `${baseURL}/internal/api/v4/vod/future-originals/${futureOriginalState.id}${query}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${getCookie("token")}`,
          },
        },
      );
      if (!response.ok) throw new Error("Request failed");

      toast.update(toastId.current, {
        render: "Future Original deleted",
        type: "success",
        isLoading: false,
        autoClose: 1000,
      });
      if (onDelete) onDelete(futureOriginalState.id);
    } catch (e) {
      toast.update(toastId.current, {
        render: "Failed to delete Future Original",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  // Notes edit handlers
  const handleEditNotesClick = (countryCode, notes) => {
    setEditingNotesCountry(countryCode);
    setNotesDraft(notes || "");
  };
  const handleNotesChange = (e) => {
    setNotesDraft(e.target.value);
  };
  const handleNotesSave = async () => {
    // Prepare the payload with all fields, updating only the root notes
    const payload = {
      content_name: futureOriginalState.content_name,
      description: futureOriginalState.description,
      is_series: futureOriginalState.is_series,
      watch_url: futureOriginalState.watch_url,
      poster_path: futureOriginalState.poster_path,
      upcoming_season_number: futureOriginalState.upcoming_season_number,
      episodes_count: futureOriginalState.episodes_count,
      provider: futureOriginalState.provider,
      imdb_id: futureOriginalState.imdb_id,
      tmdb_id: futureOriginalState.tmdb_id,
      language_code: futureOriginalState.language_code,
      source_details: futureOriginalState.source_details,
      future_original_country_mapping:
        futureOriginalState.future_original_country_mapping,
      notes: notesDraft,
    };
    try {
      toastId.current = toast.loading("Updating notes...");
      const response = await fetch(
        `${baseURL}/internal/api/v4/vod/future-originals/${futureOriginalState.id}/update`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${getCookie("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData?.message || "Failed to update notes");
      }
      await getFutureOriginal();
      toast.update(toastId.current, {
        render: "Notes updated successfully",
        type: "success",
        isLoading: false,
        autoClose: 1000,
      });
      setEditingNotesCountry(null);
    } catch (err) {
      toast.update(toastId.current, {
        render: err.message || "Failed to update notes",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
  };
  const handleNotesCancel = () => {
    setEditingNotesCountry(null);
    setNotesDraft("");
  };

  return (
    <>
      <EditFutureOriginal
        futureOriginal={futureOriginalState}
        showFilter={showEditForm}
        setShowFilter={setShowEditForm}
        notify={toast}
        getFutureOriginal={getFutureOriginal}
      />
      <EditFutureOriginal
        isCreatingDuplicate
        futureOriginal={futureOriginalState}
        showFilter={showDuplicateFOForm}
        setShowFilter={setShowDuplicateFOForm}
        notify={toast}
        getFutureOriginal={getFutureOriginal}
      />

      <div
        className={`flex gap-5 rounded border p-2 ${
          futureOriginalState.is_original
            ? "border-l-4 border-amber-500 bg-amber-50"
            : ""
        }`}
      >
        <div
          className={`flex w-36 flex-shrink-0 flex-col items-center ${futureOriginalState.is_hidden ? "opacity-50" : ""}`}
        >
          <div className="relative w-full">
            <img
              src={
                futureOriginalState.poster_path ||
                "https://placehold.co/144x216?text=No+Image"
              }
              alt={futureOriginalState.content_name}
              className="h-56 w-full rounded object-cover"
            />
            {futureOriginalState.is_original && (
              <span className="absolute left-0 top-2 rounded-r bg-amber-500 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-white shadow">
                Original
              </span>
            )}
          </div>
          {/* Notes section below image for selected country */}
          {selectedCountry &&
            (() => {
              const country =
                futureOriginalState.future_original_country_mapping.find(
                  (c) => c.country_code === selectedCountry,
                );
              if (!country) return null;
              return (
                <div className="mt-4 flex w-full flex-grow flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-semibold">Notes:</span>
                    {checkRole("internal") && (
                      <button
                        type="button"
                        className="rounded p-1 hover:bg-gray-100"
                        onClick={() =>
                          handleEditNotesClick(
                            country.country_code,
                            futureOriginalState.notes,
                          )
                        }
                        title="Edit notes"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.232 5.232l3.536 3.536M9 13h3l8-8a2.828 2.828 0 00-4-4l-8 8v3zm0 0v3h3"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="flex flex-grow flex-col">
                    {checkRole("internal") &&
                    editingNotesCountry === country.country_code ? (
                      <div className="flex h-full flex-col gap-2">
                        <textarea
                          className="block min-h-[60px] w-full flex-grow rounded border border-gray-300 px-3 py-2 text-sm"
                          value={notesDraft}
                          onChange={handleNotesChange}
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className="rounded bg-primary1 px-3 py-1 text-xs text-white"
                            onClick={handleNotesSave}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="rounded bg-gray-200 px-3 py-1 text-xs text-gray-700"
                            onClick={handleNotesCancel}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="h-full overflow-y-auto whitespace-pre-wrap break-words">
                        <span className="text-sm text-gray-700">
                          {futureOriginalState.notes ? (
                            futureOriginalState.notes
                          ) : (
                            <span className="italic text-gray-400">
                              No notes
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
        </div>
        <div className="flex w-full min-w-0 flex-col gap-2">
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <div
                className={`flex items-center gap-2 ${futureOriginalState.is_hidden ? "opacity-50" : ""}`}
              >
                <h2 className="text-lg font-semibold">
                  {futureOriginalState.content_name}
                </h2>
                {futureOriginalState.is_hidden && (
                  <span className="rounded-md bg-gray-200 px-2 py-1 text-sm font-semibold text-gray-600">
                    Hidden
                  </span>
                )}
                <p className="rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-500">
                  {futureOriginalState.is_series ? "Series" : "Movie"}
                </p>
                <p className="rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-500">
                  {futureOriginalState.provider}
                </p>
                {checkRole("internal") && 
                  <p className="rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-500">
                    {futureOriginalState.status}
                  </p>
                }
              </div>

              {checkRole("internal") && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleToggleHidden}
                    className="flex items-center gap-1 rounded-md px-3 py-1 text-sm text-primary1"
                    title={
                      futureOriginalState.is_hidden
                        ? "Unhide this Future Original"
                        : "Hide this Future Original"
                    }
                  >
                    {futureOriginalState.is_hidden ? (
                      <>
                        <HiEye className="h-4 w-4" />
                        Unhide
                      </>
                    ) : (
                      <>
                        <HiEyeOff className="h-4 w-4" />
                        Hide
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setDeleteModalOpen(true)}
                    className="flex items-center gap-1 rounded-md px-3 py-1 text-sm text-red-500"
                    title="Delete this Future Original"
                  >
                    <HiTrash className="h-4 w-4" />
                    Delete
                  </button>
                  <button
                    onClick={() => setShowDuplicateFOForm(true)}
                    className=" rounded-md px-3 py-1 text-sm text-primary1"
                  >
                    Duplicate
                  </button>
                  <button
                    onClick={() => setShowEditForm(true)}
                    className="rounded-md bg-primary1 px-3 py-1 text-sm text-white"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
            <p className={futureOriginalState.is_hidden ? "opacity-50" : ""}>
              {futureOriginalState.description}
            </p>
          </div>

          <div
            className={`flex flex-wrap gap-y-2 ${futureOriginalState.is_hidden ? "opacity-50" : ""}`}
          >
            {Array.isArray(futureOriginalState.alt_names) &&
              futureOriginalState.alt_names.length > 0 && (
                <ExpandableListDisplay
                  list={futureOriginalState.alt_names}
                  label="Alternate Names"
                />
              )}
            <p className="w-1/2">
              <span className="font-semibold">ID:</span>{" "}
              {futureOriginalState.id}
            </p>
            <p className="w-1/2">
              <span className="font-semibold">Upcoming Season:</span>{" "}
              {futureOriginalState.upcoming_season_number}
            </p>
            {futureOriginalState.is_series && (
              <p className="w-1/2">
                <span className="font-semibold">Platform Season:</span>{" "}
                {futureOriginalState.platform_season_number ?? "--"}
              </p>
            )}
            <p className="w-1/2">
              <span className="font-semibold">IMDB ID:</span>{" "}
              {futureOriginalState.imdb_id}
            </p>
            <p className="w-1/2">
              <span className="font-semibold">Episodes Count:</span>{" "}
              {futureOriginalState.episodes_count}
            </p>
            <p className="w-1/2">
              <span className="font-semibold">TMDB ID:</span>{" "}
              {futureOriginalState.tmdb_id}
            </p>
            <p className="w-1/2">
              <span className="font-semibold">Language Code:</span>{" "}
              {futureOriginalState.language_code}
            </p>
            <p className="w-1/2">
              <span className="font-semibold">Watch URL:</span>
              {futureOriginalState.watch_url ? (
                <a
                  href={futureOriginalState.watch_url}
                  className="font-bold text-blue underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Link
                </a>
              ) : (
                <span className="text-gray-500">--</span>
              )}
            </p>

            {Array.isArray(futureOriginalState.cast) &&
              futureOriginalState.cast.length > 0 && (
                <ExpandableListDisplay
                  list={futureOriginalState.cast}
                  label="Cast"
                />
              )}
            {Array.isArray(futureOriginalState.directors) &&
              futureOriginalState.directors.length > 0 && (
                <ExpandableListDisplay
                  list={futureOriginalState.directors}
                  label="Directors"
                />
              )}
            {Array.isArray(futureOriginalState.producers) &&
              futureOriginalState.producers.length > 0 && (
                <ExpandableListDisplay
                  list={futureOriginalState.producers}
                  label="Producers"
                />
              )}
            <p className="w-full">
              <span className="font-semibold">Release Dates:</span>
              <div className="mt-4 flex flex-col gap-4">
                <div className="flex gap-4">
                  {futureOriginalState.future_original_country_mapping.map(
                    (country) => (
                      <div key={country.country_code} className="flex">
                        <div>
                          <span
                            className={`cursor-pointer border-gray-900 px-2 py-1 font-semibold hover:border-b-2 ${selectedCountry === country.country_code ? "border-b-2" : ""} ${
                              checkRole("internal")
                                ? countryVerificationMap[
                                    country.country_code
                                  ] === true
                                  ? "bg-green-100"
                                  : "bg-red-100"
                                : ""
                            }`}
                            onClick={() =>
                              country.country_code === selectedCountry
                                ? setSelectedCountry(null)
                                : setSelectedCountry(country.country_code)
                            }
                          >
                            {country.country_code} -{" "}
                            {country.release_date_time
                              ? formatDate(country.release_date_time)
                              : formatDate(
                                  country.episodes_release_date_time &&
                                    country.episodes_release_date_time[0],
                                )}
                          </span>
                        </div>
                      </div>
                    ),
                  )}
                </div>
                {futureOriginalState.future_original_country_mapping.map(
                  (country, countryIndex) => (
                    <div
                      key={country.country_code}
                      className={`${selectedCountry === country.country_code ? "block" : "hidden"}`}
                    >
                      <div className="flex flex-col gap-2">
                        {country.release_date_time ? (
                          <p className="">
                            {formatDate(country.release_date_time)} -{" "}
                            <span
                              className={getStatusColor(country?.movie_status)}
                            >
                              {statusLabelMap[country.movie_status] ??
                                "Pending"}
                            </span>
                          </p>
                        ) : (
                          <div className="w-full">
                            <div className="flex flex-row justify-between gap-4">
                              {Array(4)
                                .fill()
                                .map((_, tableIndex) => {
                                  const totalEpisodes = country.release_dates
                                    ? country.release_dates.length
                                    : 0;
                                  const episodesPerTable = Math.ceil(
                                    totalEpisodes / 4,
                                  );
                                  const startIdx =
                                    tableIndex * episodesPerTable;
                                  const endIdx = Math.min(
                                    startIdx + episodesPerTable,
                                    totalEpisodes,
                                  );

                                  return (
                                    <table
                                      key={tableIndex}
                                      className={`flex-1 border border-gray-200 text-sm `}
                                    >
                                      <thead>
                                        <tr className="border-b bg-green-100">
                                          <th className="p-1 text-left">
                                            Episode
                                          </th>
                                          <th className="p-1 text-left">
                                            Release Date
                                          </th>
                                          <th className="p-1 text-left">
                                            Status
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="align-top">
                                        {country.release_dates
                                          ?.slice(startIdx, endIdx)
                                          .map((date, episodeIndex) => {
                                            return (
                                              <tr
                                                key={episodeIndex}
                                                className={`h-auto ${date?.is_verified ? "bg-green-100" : "bg-red-100"}`}
                                              >
                                                <td className="p-1">
                                                  Ep {date?.episode_number}
                                                </td>
                                                <td className="p-1">
                                                  <div className="flex items-center gap-2">
                                                    <span>
                                                      {formatDate(
                                                        date?.release_date_time,
                                                      )}
                                                    </span>
                                                  </div>
                                                </td>
                                                <td className="p-1">
                                                  <div className="flex items-center gap-2">
                                                    <span
                                                      className={getStatusColor(
                                                        date?.episode_status,
                                                      )}
                                                    >
                                                      {statusLabelMap[
                                                        date?.episode_status
                                                      ] ?? "Pending"}
                                                    </span>
                                                  </div>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                      </tbody>
                                    </table>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                        {checkRole("internal") && (
                          <div className="flex flex-col gap-2">
                            <p className="font-semibold">Is Verified:</p>
                            <div className="flex gap-4">
                              <label className="flex cursor-pointer items-center">
                                <input
                                  type="radio"
                                  checked={
                                    countryVerificationMap[
                                      country.country_code
                                    ] === true
                                  }
                                  className="mr-2"
                                  onChange={() =>
                                    handleVerifyYesClick(country.country_code)
                                  }
                                />
                                Yes
                              </label>
                              <label className="flex cursor-not-allowed items-center">
                                <input
                                  type="radio"
                                  checked={
                                    countryVerificationMap[
                                      country.country_code
                                    ] === false
                                  }
                                  className="mr-2 cursor-not-allowed"
                                  onChange={() =>
                                    handleIsVerified(
                                      country.country_code,
                                      false,
                                    )
                                  }
                                  disabled
                                />
                                No
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            </p>
          </div>
        </div>
      </div>
      {/* Confirmation Modal for Is Verified Yes */}
      <ConfirmModal
        open={confirmModalOpen}
        setOpen={setConfirmModalOpen}
        onConfirm={handleConfirmVerify}
        title="Confirm Verification"
        message="Are you sure you want to mark this country as Verified? This action cannot be undone."
        confirmText="Yes, Verify"
        cancelText="Cancel"
      />
      {/* Confirmation Modal for Delete */}
      <ConfirmModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        title="Delete Future Original"
        message="Are you sure you want to delete this Future Original? This action cannot be undone."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        reasonLabel="Reason (optional)"
        reasonPlaceholder="Reason for deleting this Future Original"
        reasonMaxLength={100}
      />
    </>
  );
}

FutureOriginalCard.propTypes = {
  onToggleHidden: PropTypes.func,
  onDelete: PropTypes.func,
  futureOriginal: PropTypes.shape({
    status: PropTypes.string,
    is_original: PropTypes.bool,
    is_hidden: PropTypes.bool,
    platform_season_number: PropTypes.number,
    content_name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    is_series: PropTypes.bool.isRequired,
    watch_url: PropTypes.string.isRequired,
    poster_path: PropTypes.string.isRequired,
    upcoming_season_number: PropTypes.number.isRequired,
    episodes_count: PropTypes.number.isRequired,
    provider: PropTypes.string.isRequired,
    imdb_id: PropTypes.string.isRequired,
    tmdb_id: PropTypes.string.isRequired,
    language_code: PropTypes.string.isRequired,
    future_original_country_mapping: PropTypes.arrayOf(
      PropTypes.shape({
        country_code: PropTypes.string.isRequired,
        release_date_time: PropTypes.string,
        episodes_release_date_time: PropTypes.arrayOf(PropTypes.string),
        movie_is_verified: PropTypes.bool,
        movie_status: PropTypes.string,
        release_dates: PropTypes.arrayOf(
          PropTypes.shape({
            release_date_time: PropTypes.string,
            episode_number: PropTypes.number.isRequired,
            episode_status: PropTypes.string,
            id: PropTypes.number.isRequired,
            is_out_of_order_approved: PropTypes.bool,
            is_out_of_order_episode: PropTypes.bool,
            is_verified: PropTypes.bool,
            updated_at: PropTypes.string,
          }),
        ),
      }),
    ).isRequired,
  }).isRequired,
};

export default FutureOriginalCard;
